import localStore from '@theme/local-store'
import { Component } from '@theme/component'

/**
 * <vehicle-selected>
 * Muestra un resumen del vehículo guardado en localStorage (clave: nomad_vehicle).
 * Se actualiza cuando se emite `vehicle:changed`.
 */
class VehicleSelected extends Component {
  constructor() {
    super()
    /** @type {(e: CustomEvent) => void} */
    this._onVehicleChanged = this._onVehicleChanged.bind(this)
    /** @type {(e: Event) => void} */
    this._onToggleClick = this._onToggleClick.bind(this)
  }

  connectedCallback() {
    super.connectedCallback?.()

    // Estructura mínima si el bloque está vacío (sin estado vacío visible)
    if (!this.querySelector('.vehicle-filter__root')) {
      this.innerHTML = `
        <div class="vehicle-filter__root">
          <div class="vehicle-filter__content" ref="content" hidden style="display: flex; justify-content: center; align-items: center; flex-direction: column;">
            <div class="vehicle-filter__media" ref="media" hidden style="max-width: 200px;">
              <img ref="image" alt="Vehículo seleccionado" loading="lazy" />
            </div>
            <div class="vehicle-filter__text center">
              <span class="vehicle-filter__make" ref="make"></span>
              <span aria-hidden="true"> - </span>
              <strong class="vehicle-filter__label" ref="label"></strong>
            </div>
          </div>
        </div>
      `
    }

    requestAnimationFrame(() => this._render())
    document.addEventListener('vehicle:changed', this._onVehicleChanged)
    this.addEventListener('click', this._onToggleClick, { capture: true })
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:changed', this._onVehicleChanged)
    this.removeEventListener('click', this._onToggleClick, { capture: true })
    super.disconnectedCallback?.()
  }

  /** @param {CustomEvent} _e */
  _onVehicleChanged(_e) {
    this._render()
  }

  /**
   * Toggle del anuncio de vehículo (abre/cierra <details.announcement-vehicle>)
   * @param {Event} e
   */
  _onToggleClick(e) {
    const target = /** @type {Element|null} */ (e.composedPath?.()[0] ?? e.target)
    if (!(target instanceof Element)) return

    // Evitar que clics en enlaces/botones internos cambien el estado por accidente
    if (target.closest('a, button, [role="button"]')) return

    try {
      // Buscar el slide visible del announcement bar y su details
      let details = /** @type {HTMLDetailsElement|null} */ (
        document.querySelector('.announcement-bar__slides [aria-hidden="false"] .announcement-vehicle')
      )
      if (!details) {
        // Fallback: primer details en el documento
        details = /** @type {HTMLDetailsElement|null} */ (
          document.querySelector('.announcement-vehicle')
        )
      }
      if (!details) return

      details.open = !details.open

      if (details.open) {
        try {
          details.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
        } catch (_) {}
      }
    } catch (_) {
      // noop
    }
  }

  _render() {
    /** @type {HTMLElement|null} */
    const content = this.querySelector('[ref="content"]')
    /** @type {HTMLImageElement|null} */
    const image = this.querySelector('[ref="image"]')
    /** @type {HTMLElement|null} */
    const media = this.querySelector('[ref="media"]')
    /** @type {HTMLElement|null} */
    const makeEl = this.querySelector('[ref="make"]')
    /** @type {HTMLElement|null} */
    const labelEl = this.querySelector('[ref="label"]')

    if (!content) return

    const v = this._readVehicle()

    if (!v?.vehicle && !v?.label && !v?.name) {
      // Ocultar completamente el componente cuando está vacío
      this.hidden = true
      return
    }

    // Asegurar que el componente sea visible cuando haya datos
    this.hidden = false

    const label = this._decode(
      (v.label || v.name || this._capitalize(v.vehicle)).trim()
    )
    const make = this._decode(
      (v.make || localStore.get('vehicle_make', '') || '').trim()
    )

    if (labelEl) {
      labelEl.textContent = label
      labelEl.setAttribute('title', label)
    }
    if (makeEl) {
      const text = make ? this._capitalize(make) : ''
      makeEl.textContent = text
      makeEl.setAttribute('title', text)
    }

    // Imagen opcional
    const imgSrc = (v.image || '').trim()
    if (image && media) {
      if (imgSrc) {
        image.src = imgSrc
        image.alt = label
          ? `Vehículo seleccionado: ${label}`
          : 'Vehículo seleccionado'
        image.hidden = false
        media.hidden = false
      } else {
        image.removeAttribute('src')
        image.hidden = true
        media.hidden = true
      }
    }

    content.hidden = false
  }

  _readVehicle() {
    try {
      let v = localStore.get('vehicle', null)
      if (typeof v === 'string') {
        try {
          v = JSON.parse(v)
        } catch (_) {
          // Si fuera un string simple, asúmelo como handle
          v = { vehicle: String(v || '').trim() }
        }
      }
      if (v && typeof v === 'object') return v
      return null
    } catch (_) {
      return null
    }
  }

  _capitalize(handle) {
    if (!handle) return ''
    return String(handle)
      .split('-')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  }

  // Decodifica entidades HTML si la cadena viene escapada (e.g., &quot;)
  _decode(str) {
    if (!str) return ''
    try {
      const t = document.createElement('textarea')
      t.innerHTML = String(str)
      return t.value
    } catch (_) {
      return String(str)
    }
  }
}

if (!customElements.get('vehicle-selected')) {
  customElements.define('vehicle-selected', VehicleSelected)
}
